import { createAsyncThunk } from "@reduxjs/toolkit";
import { iStateMessage } from "@/customTypes/GenericReduxTypes";
import { iGenericResponse } from "@/customTypes/CommonServiceTypes";
import { curryGetThunkName } from "@/projectUtils/ReduxUtil";
import { httpStatusCodes } from "@/customTypes/NetworkTypes";
import { employeeKpiService } from "@/services/api/cresivoxServer";
import { REDUCER_NAME } from "./Types";

const curriedGetThunkName = curryGetThunkName(REDUCER_NAME);

type R = iGenericResponse<any | null> | null;
type Rej = { rejectValue: iStateMessage };

const mkThunk = <T = void>(name: string, fn: (arg: T) => Promise<any>) =>
  createAsyncThunk<R, T, Rej>(curriedGetThunkName(name), async (arg, { rejectWithValue }) => {
    try {
      const response = await fn(arg);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if ([httpStatusCodes.SUCCESS_OK, httpStatusCodes.SUCCESS_CREATED].includes(httpStatusCode)) {
          return { data: { httpStatusCode, data: data?.data, message } };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch { return rejectWithValue({ message: "Something went wrong" }); }
  });

export const getTemplates = mkThunk<void>('getTemplates', () => employeeKpiService.getTemplates());

export const saveTemplate = mkThunk<{ payload: any; id?: string }>(
  'saveTemplate', ({ payload, id }) =>
    id ? employeeKpiService.updateTemplate(id, payload) : employeeKpiService.saveTemplate(payload)
);

export const deleteTemplate = mkThunk<string>('deleteTemplate', (id) => employeeKpiService.deleteTemplate(id));

export const getAssignments = mkThunk<string>('getAssignments', (employeeId) => employeeKpiService.getAssignments(employeeId));

export const upsertAssignment = mkThunk<{ employeeId: string; templateId: string; target_value: number }>(
  'upsertAssignment', ({ employeeId, templateId, target_value }) =>
    employeeKpiService.upsertAssignment(employeeId, templateId, target_value)
);

export const logKpi = mkThunk<any>('logKpi', (payload) => employeeKpiService.logKpi(payload));

export const getPerformance = mkThunk<{ employeeId: string; period_date?: string; period_type?: string }>(
  'getPerformance', ({ employeeId, period_date, period_type }) =>
    employeeKpiService.getPerformance(employeeId, period_date, period_type)
);

export const getTeamPerformance = mkThunk<{ period_date?: string; period_type?: string } | void>(
  'getTeamPerformance', (p) => employeeKpiService.getTeamPerformance(p?.period_date, p?.period_type)
);

export const getAttendance = mkThunk<{ employeeId: string; month?: string }>(
  'getAttendance', ({ employeeId, month }) => employeeKpiService.getAttendance(employeeId, month)
);

export const markAttendance = mkThunk<any>('markAttendance', (payload) => employeeKpiService.markAttendance(payload));
